import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AUTH_CONFIG, type AuthConfig } from '@olv/ui-shared/features/auth';
import {
  AcknowledgeIncompleteRiskDto,
  AdjustTransactionRequestDto,
  ApproveTransactionRequestDto,
  CancelTransactionDto,
  PrepareDepositRequest,
  PrepareDepositResponse,
  PrepareTransactionRequestDto,
  RejectTransactionRequestDto,
  RetryTransactionDto,
  ReverseTransactionDto,
  SetTransactionRiskRequestDto,
} from '../models/merchant-transaction.model';
import {
  SourcingWalletsRequestDto,
  SourcingWalletsResponseDto,
  WithdrawalAvailableBalanceDto,
  WithdrawalExecutionPlanDto,
  WithdrawalExecutionPlanRequestDto,
} from '../models/sourcing-wallet.model';
import { AppContextService } from './app-context.service';
import { SetExchangeRateDto } from './system-transaction.service';

@Injectable({
  providedIn: 'root',
})
export class MerchantTransactionService {
  private readonly appCtx = inject(AppContextService);
  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);
  private readonly http = inject(HttpClient);
  private readonly isMerchantApp = this.appCtx.getAppName() === 'merchant';
  private readonly baseUrl = this.isMerchantApp
    ? `${this.cfg.baseUrl}/api/Transaction`
    : `${this.cfg.baseUrl}/api/MerchantTransaction`;

  // The merchant app scopes withdrawal endpoints by auth claims; the settlement
  // app scopes them by an explicit /{merchantId} segment.
  private withdrawalRoot(merchantId?: string): string {
    return this.isMerchantApp ? `${this.baseUrl}/withdrawal` : `${this.baseUrl}/${merchantId}/withdrawal`;
  }

  /**
   * Sends a prepare transaction request to the backend
   * @param prepareTransactionRequest The transaction details to prepare
   * @returns Observable with the transaction ID in the response
   */
  prepareTransaction(prepareTransactionRequest: PrepareTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/prepare`;
    return this.http.post<any>(url, prepareTransactionRequest);
  }

  /**
   * Gets the status of a transaction
   * @param transactionId The ID of the transaction to check
   * @returns Observable with transaction details
   */
  getTransactionStatus(transactionId: string): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/status`;
    return this.http.get<any>(url);
  }

  /**
   * Approves a pending transaction
   * @param transactionId The ID of the transaction to approve
   * @param approveRequest The approval details including comments
   * @returns Observable of the response
   */
  approveTransaction(transactionId: string, approveRequest: ApproveTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/approve`;
    return this.http.post<any>(url, approveRequest);
  }

  /**
   * Reject a pending transaction
   * @param transactionId The ID of the transaction to approve
   * @param rejectRequest The approval details including comments
   * @returns Observable of the response
   */
  rejectTransaction(transactionId: string, rejectRequest: RejectTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/reject`;
    return this.http.post<any>(url, rejectRequest);
  }

  /**
   * Adjust transaction
   * @param adjustRequest The adjustment details including type, amount, reason, totp
   * @returns Observable of the response
   */
  adjustTransaction(adjustRequest: AdjustTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/adjustments/prepare`;
    return this.http.post<any>(url, adjustRequest);
  }

  /**
   * Approves a Adjusted transaction
   * @param transactionId The ID of the transaction to approve
   * @param approveRequest The approval details including totp,comments
   * @returns Observable of the response
   */
  approveAdjustTransaction(transactionId: string, approveRequest: ApproveTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/adjustments/${transactionId}/approve`;
    return this.http.post<any>(url, approveRequest);
  }

  /**
   * Reject a Adjusted transaction
   * @param transactionId The ID of the transaction to reject
   * @param rejectRequest The reject details including comments
   * @returns Observable of the response
   */
  rejectAdjustTransaction(transactionId: string, rejectRequest: RejectTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/adjustments/${transactionId}/reject`;
    return this.http.post<any>(url, rejectRequest);
  }

  approveDeposit(transactionId: string, approveRequest: ApproveTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/approve-deposit`;
    return this.http.post<any>(url, approveRequest);
  }

  rejectDeposit(transactionId: string, rejectRequest: RejectTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/reject-deposit`;
    return this.http.post<any>(url, rejectRequest);
  }

  reverseDeposit(transactionId: string, reverseRequest: ReverseTransactionDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/reverse-deposit`;
    return this.http.post<any>(url, reverseRequest);
  }

  cancelTransaction(transactionId: string, request: CancelTransactionDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/cancel`;
    return this.http.post<any>(url, request);
  }

  cancelPreparedTransaction(transactionId: string, rejectRequest: RejectTransactionRequestDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/cancel-prepared`;
    return this.http.post<any>(url, rejectRequest);
  }

  speedupTransaction(transactionId: string, request: CancelTransactionDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/speed-up`;
    return this.http.post<any>(url, request);
  }

  retryTransaction(transactionId: string, retryRequest: RetryTransactionDto): Observable<any> {
    const url = `${this.baseUrl}/${transactionId}/retry`;
    return this.http.post<any>(url, retryRequest);
  }

  prepareDeposit(request: PrepareDepositRequest): Observable<PrepareDepositResponse> {
    return this.http.post<PrepareDepositResponse>(`${this.baseUrl}/prepare-deposit`, request);
  }

  setRisk(transactionId: string, request: SetTransactionRiskRequestDto): Observable<void> {
    const url = `${this.baseUrl}/${transactionId}/set-risk`;
    return this.http.post<void>(url, request);
  }

  acknowledgeIncompleteRisk(
    transactionId: string,
    taskId: string,
    request: AcknowledgeIncompleteRiskDto,
  ): Observable<void> {
    const url = `${this.baseUrl}/${transactionId}/acknowledge-incomplete-risk/${taskId}`;
    return this.http.post<void>(url, request);
  }

  setExchangeRate(transactionId: string, request: SetExchangeRateDto): Observable<void> {
    const url = `${this.baseUrl}/${transactionId}/exchange-rate`;
    return this.http.post<void>(url, request);
  }

  /**
   * Resolves the sourcing wallets for an automatic-routing withdrawal.
   *
   * The backend computes which source wallets will fund the selected destination
   * wallet and returns them as siblings of a single-level routing tree (their
   * parent being the destination wallet). The result is visualised on the
   * withdrawal summary screen.
   *
   * @param request The destination wallet, currency, amount and risk group.
   * @param merchantId Required for the settlement app, which scopes the route by merchant.
   */
  getSourcingWallets(request: SourcingWalletsRequestDto, merchantId?: string): Observable<SourcingWalletsResponseDto> {
    const url = `${this.withdrawalRoot(merchantId)}/sourcing-wallets`;
    return this.http.post<SourcingWalletsResponseDto>(url, request);
  }

  /**
   * Returns the per-risk-group and cumulative available balances for an automatic withdrawal.
   *
   * @param currencyId The currency the withdrawal is denominated in.
   * @param merchantId Required for the settlement app, which scopes the route by merchant.
   */
  getAutomaticWithdrawalAvailableBalances(
    currencyId: string,
    merchantId?: string,
  ): Observable<WithdrawalAvailableBalanceDto[]> {
    const url = `${this.withdrawalRoot(merchantId)}/available-balances`;
    const params = new HttpParams().set('currencyId', currencyId);
    return this.http.get<WithdrawalAvailableBalanceDto[]>(url, { params });
  }

  /**
   * Computes the execution plan (fees, coverage, per-group contributions) for an automatic withdrawal.
   *
   * @param request The currency, amount and selected risk group.
   * @param merchantId Required for the settlement app, which scopes the route by merchant.
   */
  getAutomaticWithdrawalExecutionPlan(
    request: WithdrawalExecutionPlanRequestDto,
    merchantId?: string,
  ): Observable<WithdrawalExecutionPlanDto> {
    const url = `${this.withdrawalRoot(merchantId)}/execution-plan`;
    return this.http.post<WithdrawalExecutionPlanDto>(url, request);
  }
}
